import { AlertTriangle, CheckCircle2, Clock3, FileText, Plus, RotateCcw, Trash2 } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { confirmModal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { fmtDate } from '../../../shared/format'
import { DOC_STATUSES, type Installation, type Project } from '../../types'
import { toastError, useStaffLookup } from '../../ui'
import { SEVERITY_TONE } from '../../visuals'
import { useInstallationActions } from '../../workApi'
import { DocStatusBadge, SeverityBadge } from '../StatusBadges'
import { BlockerForm, DocForm } from './SideForms'

type Props = { project: Project; installation: Installation; canWrite: boolean }

function Card({ icon: Icon, tone, title, action, children }: { icon: typeof Clock3; tone: string; title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-xl border bg-card shadow-card">
      <header className="flex items-center gap-2 border-b px-4 py-3">
        <Icon className={cn('size-4', tone)} aria-hidden />
        <h3 className="min-w-0 flex-1 truncate text-sm font-semibold">{title}</h3>
        {action}
      </header>
      <div className="space-y-2 p-3">{children}</div>
    </section>
  )
}

export function InstallationSidebar({ project, installation: inst, canWrite }: Props) {
  const { t } = useTranslation()
  const { nameOf } = useStaffLookup()
  const actions = useInstallationActions(project.id, inst.id)
  const [form, setForm] = useState<'blocker' | 'doc' | null>(null)
  const open = inst.blockers.filter((b) => !b.resolved)
  const approvals = project.tasks.filter((x) => x.installationId === inst.id && x.status === 'approval')
  const empty = (text: string) => <p className="px-1 py-3 text-center text-xs text-muted-foreground">{text}</p>
  const add = (kind: 'blocker' | 'doc', label: string) =>
    canWrite ? (
      <Button size="icon" variant="ghost" className="size-7" onClick={() => setForm(kind)} aria-label={label}>
        <Plus />
      </Button>
    ) : null

  const confirmDelete = async (title: string, description: string, run: () => Promise<unknown>) => {
    if (!(await confirmModal({ title, description, confirmLabel: t('common.delete'), destructive: true }))) return
    try {
      await run()
      toast.success(t('common.deleted'))
    } catch (e) {
      toastError(e)
    }
  }

  return (
    <aside className="space-y-4">
      <Card icon={AlertTriangle} tone="text-destructive" title={t('projects.install.blockers', { count: open.length })} action={add('blocker', t('projects.install.form.blockerTitle'))}>
        {inst.blockers.length === 0 ? empty(t('projects.install.noBlockers')) : null}
        {inst.blockers.map((b) => (
          <div key={b.id} className={cn('flex items-start gap-3 rounded-lg border p-3', b.resolved && 'opacity-60')}>
            <span className={cn('flex size-8 shrink-0 items-center justify-center rounded-lg', b.resolved ? 'bg-success/10 text-success' : SEVERITY_TONE[b.severity])}>
              {b.resolved ? <CheckCircle2 className="size-4" aria-hidden /> : <AlertTriangle className="size-4" aria-hidden />}
            </span>
            <div className="min-w-0 flex-1 space-y-2">
              <div className="flex items-start gap-2">
                <p className={cn('min-w-0 flex-1 text-sm font-medium leading-snug', b.resolved && 'line-through')}>{b.text}</p>
                <SeverityBadge severity={b.severity} />
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {b.ownerId ? <Avatar name={nameOf(b.ownerId)} className="size-5 [&>span]:text-[9px]" /> : null}
                <span className="min-w-0 flex-1 truncate">{b.ownerId ? nameOf(b.ownerId) : '—'}</span>
                <span className="tabular-nums">{fmtDate(b.date)}</span>
                {canWrite ? (
                  <>
                    <Button size="icon" variant="ghost" className="size-7" aria-label={t(b.resolved ? 'projects.install.reopen' : 'projects.install.resolve')} onClick={() => actions.toggleBlocker.mutate(b.id, { onError: toastError })}>
                      {b.resolved ? <RotateCcw /> : <CheckCircle2 />}
                    </Button>
                    <Button size="icon" variant="ghost" className="size-7 hover:text-destructive" aria-label={t('common.delete')} onClick={() => void confirmDelete(t('projects.install.blockerDeleteTitle'), b.text, () => actions.deleteBlocker.mutateAsync(b.id))}>
                      <Trash2 />
                    </Button>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        ))}
      </Card>

      <Card icon={Clock3} tone="text-violet" title={t('projects.install.approvals', { count: approvals.length })}>
        {approvals.length === 0 ? empty(t('projects.install.noApprovals')) : null}
        {approvals.map((x) => (
          <Link key={x.id} to={`/projects/${project.id}/tasks/${x.id}`} className="flex items-start gap-3 rounded-lg border p-3 transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-violet/10 text-violet">
              <Clock3 className="size-4" aria-hidden />
            </span>
            <div className="min-w-0 flex-1 space-y-1">
              <p className="text-sm font-medium leading-snug">{x.title}</p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="font-mono">{x.key}</span>
                <span className="min-w-0 flex-1 truncate">{x.reviewerId ? nameOf(x.reviewerId) : '—'}</span>
                <span className="tabular-nums">{fmtDate(x.dueDate)}</span>
              </div>
            </div>
          </Link>
        ))}
      </Card>

      <Card icon={FileText} tone="text-primary" title={t('projects.install.documents')} action={add('doc', t('projects.install.form.docTitle'))}>
        {inst.documents.length === 0 ? empty(t('projects.install.noDocs')) : null}
        {inst.documents.map((d) => (
          <div key={d.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border p-3">
            <FileText className="size-6 shrink-0 text-muted-foreground" aria-hidden />
            <div className="min-w-0 flex-1 basis-32">
              <p className="truncate text-sm font-medium">{d.name}</p>
              <p className="text-xs tabular-nums text-muted-foreground">{fmtDate(d.date)}</p>
            </div>
            {canWrite ? (
              <>
                <Select
                  size="xs"
                  className="w-28"
                  value={d.status}
                  aria-label={t('projects.install.columns.status')}
                  options={DOC_STATUSES.map((s) => ({ value: s, label: t(`projects.docStatus.${s}`) }))}
                  onChange={(status) => actions.saveDoc.mutate({ id: d.id, input: { name: d.name, date: d.date, status } }, { onError: toastError })}
                />
                <Button size="icon" variant="ghost" className="size-7 hover:text-destructive" aria-label={t('common.delete')} onClick={() => void confirmDelete(t('projects.install.docDeleteTitle'), d.name, () => actions.deleteDoc.mutateAsync(d.id))}>
                  <Trash2 />
                </Button>
              </>
            ) : (
              <DocStatusBadge status={d.status} />
            )}
          </div>
        ))}
      </Card>

      {form === 'blocker' ? <BlockerForm project={project} installation={inst} onDone={() => setForm(null)} /> : null}
      {form === 'doc' ? <DocForm project={project} installation={inst} onDone={() => setForm(null)} /> : null}
    </aside>
  )
}
